import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Wifi, RefreshCw } from 'lucide-react';

interface ConnectingScreenProps {
  onRetry?: () => void;
  retryCount?: number;
}

/**
 * Phase 2 UI — friendly "Connecting..." screen.
 * Shown during medium-duration outages (after silent phase, before full error).
 * VideoGame-themed, i18n-supported, RTL-safe (centered layout).
 */
export function ConnectingScreen({ onRetry, retryCount = 0 }: ConnectingScreenProps) {
  const { t } = useTranslation('common');

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: '#0D0D1A' }}
    >
      <div className="flex flex-col items-center max-w-sm w-full text-center">
        {/* Pulsing WiFi icon */}
        <motion.div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-8"
          style={{
            background: 'rgba(244, 211, 94, 0.15)',
            border: '2px solid rgba(244, 211, 94, 0.3)',
          }}
          animate={{
            scale: [1, 1.08, 1],
            opacity: [0.8, 1, 0.8],
          }}
          transition={{
            repeat: Infinity,
            duration: 2,
            ease: 'easeInOut',
          }}
        >
          <Wifi className="w-10 h-10" style={{ color: '#F4D35E' }} />
        </motion.div>

        {/* Animated dots title */}
        <h2
          className="text-xl font-bold uppercase tracking-wider mb-3"
          style={{ color: '#FFFFFF' }}
        >
          {t('connectionError.connectingDots')}
          <AnimatedDots />
        </h2>

        {/* Subtitle */}
        <p className="text-sm mb-8" style={{ color: 'rgba(255,255,255,0.6)' }}>
          {t('connectionError.pleaseWait')}
        </p>

        {/* Subtle progress bar (15s animation loop) */}
        <div
          className="w-full h-1 rounded-full overflow-hidden mb-6"
          style={{ background: 'rgba(255,255,255,0.1)' }}
        >
          <motion.div
            className="h-full rounded-full"
            style={{ background: '#F4D35E' }}
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        </div>

        {/* Retry count */}
        {retryCount > 0 && (
          <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {t('connectionError.retryAttempt', { count: retryCount })}
          </p>
        )}

        {/* Manual retry button */}
        <motion.button
          onClick={onRetry}
          className="py-2.5 px-6 font-bold text-xs uppercase tracking-wider flex items-center gap-2 rounded-lg"
          style={{
            background: 'transparent',
            color: '#F4D35E',
            border: '2px solid rgba(244, 211, 94, 0.4)',
          }}
          whileHover={{
            scale: 1.02,
            borderColor: 'rgba(244, 211, 94, 0.8)',
          }}
          whileTap={{ scale: 0.98 }}
        >
          <RefreshCw className="w-4 h-4" />
          <span>{t('connectionError.retryNow')}</span>
        </motion.button>
      </div>
    </div>
  );
}

/** Renders cycling "..." animation */
function AnimatedDots() {
  return (
    <motion.span
      animate={{ opacity: [0, 1, 0] }}
      transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
    >
      ...
    </motion.span>
  );
}
